import { Box, DialogActions, DialogContent, Stack } from '@mui/material'
import { ReactElement, useState } from 'react'
import { Form } from 'react-final-form'
import { Mutation_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { insertFactsheetUser } from 'src/screens/factsheets/factsheet/permissions/factsheetPermissionsQuery'
import { PrimaryButton, SecondaryButton } from 'src/shared/button/Buttons'
import { TEXT_LENGTH, FACTSHEET_USER_ROLES, FACTSHEET_USER_ROLE_TYPE } from 'src/shared/constants/constants'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { TextField } from 'src/shared/form/control/TextField'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { composeValidators, maxChar, required, validEmail } from 'src/shared/form/validation/validators'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'

interface AddNewUserFactsheetFormValues {
  email: string
  permissionOptionCode: FACTSHEET_USER_ROLE_TYPE
}

interface NewFactsheetUser {
  factsheetId: number
  type: FACTSHEET_USER_ROLE_TYPE
  email: string
}

interface AddNewUserFactsheetModalDialogProps {
  onCancel: () => void
  onSuccess: () => void
  factsheetId: number
}

const decorators = createDecorators()

export const AddNewUserFactsheetModalDialog = ({
  onCancel,
  onSuccess,
  factsheetId,
}: AddNewUserFactsheetModalDialogProps): ReactElement => {
  const { getMessage } = useMessageSource()
  const notificationService = useNotificationService()
  const [permissionOptions] = useState<{ label: string; value: string | number }[]>([
    { label: getMessage('label.can.edit'), value: FACTSHEET_USER_ROLES.CONTRIBUTOR },
    { label: getMessage('label.can.view'), value: FACTSHEET_USER_ROLES.READER },
  ])
  const urqlClient = useClient()

  const handleSubmitLocal = async (values: AddNewUserFactsheetFormValues): Promise<any> => {
    const { data, error } = await urqlClient
      .mutation<{ addUserToFactsheet: Mutation_Root['addUserToFactsheet'] }, NewFactsheetUser>(insertFactsheetUser, {
        factsheetId: factsheetId,
        email: values.email,
        type: values.permissionOptionCode,
      })
      .toPromise()

    if (error || data?.addUserToFactsheet?.status === 'FAILURE') {
      notificationService.operationFailed()
    } else {
      notificationService.changesSaved()
      onSuccess()
    }
  }

  return (
    <>
      <Box>
        <Form<AddNewUserFactsheetFormValues>
          onSubmit={handleSubmitLocal}
          decorators={decorators}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <DialogContent>
                <Stack spacing={2}>
                  <TextField
                    label={getMessage('label.email')}
                    required
                    name="email"
                    validate={composeValidators(required(), validEmail(), maxChar(TEXT_LENGTH.S))}
                  />
                  <AutoCompleteField
                    required
                    label={getMessage('label.select.permission')}
                    name="permissionOptionCode"
                    options={permissionOptions}
                    validate={required()}
                  />
                </Stack>
              </DialogContent>
              <DialogActions>
                <SecondaryButton messageKey={'button.cancel'} onClick={onCancel} />
                <PrimaryButton messageKey={'button.add'} type="submit" />
              </DialogActions>
            </form>
          )}
        />
      </Box>
    </>
  )
}
